import "./styles.css";
import { cn } from "@/libraries/cn";
import { parseDate } from "@/libraries/dayjs-relative";
import { transformKeysToSnakeCase } from "@/libraries/transformers";
import { getCalendarInfoQueryOptions } from "@/queries/options/calendar";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { PickersCalendarHeaderProps } from "@mui/x-date-pickers/PickersCalendarHeader";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import type { PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import { useSuspenseQuery } from "@tanstack/react-query";
import type dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer } from "vaul";
import { Route } from "..";

interface Props {
	selectedDate: Dayjs;
}

const CalendarHeader = (props: PickersCalendarHeaderProps<dayjs.Dayjs>) => {
	const { currentMonth, onMonthChange } = props;

	const handlePrevMonth = () =>
		onMonthChange(currentMonth.subtract(1, "month"), "left");
	const handleNextMonth = () =>
		onMonthChange(currentMonth.add(1, "month"), "right");

	return (
		<Box
			display="flex"
			alignItems="center"
			justifyContent="space-between"
			px={2}
			py={1}
		>
			<IconButton onClick={handlePrevMonth}>
				<ArrowBackIosNewIcon fontSize="small" />
			</IconButton>

			<Typography fontWeight={400} fontSize={16}>
				{currentMonth.format("MMMM YYYY")}
			</Typography>

			<IconButton onClick={handleNextMonth}>
				<ArrowForwardIosIcon fontSize="small" />
			</IconButton>
		</Box>
	);
};

const CalendarDay = ({
	className,
	highlightedDays = [],
	...props
}: PickersDayProps<dayjs.Dayjs> & {
	highlightedDays?: {
		date: dayjs.Dayjs;
		event: number;
		todo: number;
	}[];
}) => {
	const highlightedDay =
		!props.outsideCurrentMonth &&
		highlightedDays.find((d) => props.day.date() === d.date.date());

	return (
		<Badge
			key={props.day.toString()}
			overlap="circular"
			anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
			badgeContent={
				highlightedDay ? (
					<span className="flex justify-center items-center gap-1">
						{highlightedDay.todo > 0 && (
							<span className="w-[6px] h-[6px] rounded-full bg-success-200" />
						)}
						{highlightedDay.event > 0 && (
							<span
								className={cn("w-[6px] h-[6px] rounded-full bg-primary", {
									"!bg-white": props.selected,
								})}
							/>
						)}
					</span>
				) : undefined
			}
			sx={{
				"& .MuiBadge-badge": {
					bottom: -3,
					left: "50%",
					transform: "translateX(-50%)",
					top: "auto",
					right: "auto",
				},
			}}
		>
			<PickersDay
				className={cn(
					"!text-sm !font-normal [&.Mui-selected]:!font-bold",
					className,
				)}
				{...props}
			/>
		</Badge>
	);
};

const DrawerContent = ({
	isFullOpen,
	selectedDate,
}: { isFullOpen: boolean; selectedDate: dayjs.Dayjs }) => {
	const calendarContainerRef = useRef<HTMLDivElement>(null);
	const navigate = Route.useNavigate();

	const deps = Route.useLoaderDeps({
		select: (s) => s,
	});

	const calendarInfoQuery = useSuspenseQuery(
		getCalendarInfoQueryOptions(transformKeysToSnakeCase(deps)),
	);

	const highlightedDays = useMemo(
		() =>
			calendarInfoQuery.data.calendars.flatMap((entry) => {
				const [date, info] = Object.entries(entry)[0];
				return {
					date: parseDate(date, "YYYY-MM-DD"),
					event: info.event_count,
					todo: info.reminder_count,
				};
			}),
		[calendarInfoQuery.data],
	);

	const onChangeDay = useCallback(
		(val: Dayjs) => {
			const valFormat = val.format("DD-MM-YYYY");
			return navigate({
				replace: true,
				search: {
					date: valFormat,
				},
			});
		},
		[navigate],
	);

	const handleWeeks = useCallback(
		(calendarEl: Element, isExpanded: boolean) => {
			const weekContainers = calendarEl.querySelectorAll(
				".MuiDayCalendar-weekContainer",
			);
			if (!weekContainers.length) return;

			let selectedWeekFound = false;

			for (const week of weekContainers) {
				const el = week as HTMLElement;

				if (isExpanded) {
					el.classList.remove("week-hidden");
					el.classList.add("week-visible");
				} else {
					const hasSelected = week.querySelector('[aria-selected="true"]');
					if (hasSelected) {
						el.classList.remove("week-hidden");
						el.classList.add("week-visible");
						selectedWeekFound = true;
					} else {
						el.classList.remove("week-visible");
						el.classList.add("week-hidden");
					}
				}
			}

			if (!isExpanded && !selectedWeekFound && weekContainers.length > 0) {
				const firstWeek = weekContainers[0] as HTMLElement;
				firstWeek.classList.remove("week-hidden");
				firstWeek.classList.add("week-visible");
			}
		},
		[],
	);

	useEffect(() => {
		const containerEl = calendarContainerRef.current;
		if (!containerEl) return;

		const calendarEl = containerEl.querySelector(".MuiDateCalendar-root");
		if (!calendarEl) return;

		const observer = new MutationObserver(() =>
			handleWeeks(calendarEl, isFullOpen),
		);
		observer.observe(calendarEl, { childList: true, subtree: true });
		handleWeeks(calendarEl, isFullOpen); // Initial run

		return () => observer.disconnect();
	}, [isFullOpen, handleWeeks]);

	return (
		<div
			ref={calendarContainerRef}
			className="container flex flex-col mx-auto w-full overflow-hidden"
		>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DateCalendar
					view="day"
					value={selectedDate}
					showDaysOutsideCurrentMonth
					fixedWeekNumber={6}
					slots={{
						calendarHeader: CalendarHeader,
						day: CalendarDay,
					}}
					slotProps={{
						day: {
							highlightedDays: highlightedDays,
						} as any,
					}}
					dayOfWeekFormatter={(weekday) => weekday.format("ddd")}
					onChange={onChangeDay}
					className={cn({ "!h-36": !isFullOpen })}
				/>
			</LocalizationProvider>
		</div>
	);
};

const snapPoints = ["225px", "420px"] as const;

const SwipeableTopDrawer = ({ selectedDate }: Props) => {
	const { t } = useTranslation("dashboard");
	const [snap, setSnap] = useState<number | string | null>(snapPoints[0]);

	const isOpenBackdrop = useMemo(() => snap === snapPoints[1], [snap]);

	return (
		<>
			<Drawer.Root
				defaultOpen
				modal={false}
				direction="top"
				dismissible={false}
				snapPoints={snapPoints as unknown as string[]}
				activeSnapPoint={snap}
				setActiveSnapPoint={setSnap}
				fadeFromIndex={1}
				noBodyStyles
			>
				<Drawer.Portal>
					<Drawer.Content className="wrapper bg-white rounded-xl pb-2 fixed flex flex-col-reverse bottom-0 left-0 right-0 h-full max-h-[97%] mx-auto">
						<Drawer.Handle className="cursor-grab" />
						<Drawer.Title className="hidden">
							{t("dashboard:calendar.dateDrawerTitle")}
						</Drawer.Title>
						<Drawer.Description className="hidden">
							{t("dashboard:calendar.dateDrawerDescription")}
						</Drawer.Description>
						<DrawerContent
							isFullOpen={snap === snapPoints[1]}
							selectedDate={selectedDate}
						/>
					</Drawer.Content>
				</Drawer.Portal>
			</Drawer.Root>
			{isOpenBackdrop && (
				// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
				<div
					onClick={() => setSnap(snapPoints[0])}
					className="absolute inset-0 bg-black/40 z-0 transition-opacity duration-300 ease-in-out opacity-100 pointer-events-auto"
				/>
			)}
		</>
	);
};

export default SwipeableTopDrawer;
